import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { HiOutlineNewspaper } from "react-icons/hi2";
import { Link, useNavigate } from "react-router-dom";
import { deleteBlog, fetchBlogsByCreator, resetValues } from "redux/features/blog/blogSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import DashboardMenuCreator from "./DashboardPage/DashboardMenu";

export interface ListArticlesPageProps {
  className?: string;
}

interface Blog {
  id: number;
  title: string;
  blog_url: string;
  description: string;
  tags: any;
}

interface ModalState {
  isDeleteModalOpen: boolean;
  selectedItem: Blog | null;
  loading: boolean;
}

const ListArticles: FC<ListArticlesPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    blogs: blogsList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    blogs: state.featureBlog?.allBlogs?.blogs || [],
    totalCount: state.featureBlog?.allBlogs?.totalCount || 0,
    totalPages: state.featureBlog?.allBlogs?.totalPages || 0,
    currentPage: state.featureBlog?.allBlogs?.currentPage || 1,
    loading: state.featureBlog?.loading || "idle",
  }));
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isDeleteModalOpen: false,
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(fetchBlogsByCreator({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    dispatch(resetValues());
  }, [dispatch]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteModalOpen = (blog: Blog) => {
    setModalState({ ...modalState, selectedItem: blog, isDeleteModalOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteBlog(modalState.selectedItem.id));
      setModalState({
        ...modalState,
        isDeleteModalOpen: false,
        loading: false,
        selectedItem: null,
      });
      await dispatch(fetchBlogsByCreator({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleTableAction = (action: string, item: Blog) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "edit") {
      navigate(`/dashboard/update-article/${item.id}`);
    } else if (action === "view") {
      navigate(`/blog-single/${item.id}`);
    }
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (item: Blog) => (
        <img
          src={`${process.env.REACT_APP_CLOUD_FRONT}${item.blog_url}`}
          alt={item.title}
          className="object-cover w-16 h-16 rounded"
        />
      ),
    },
    {
      key: "title",
      label: "Titre",
      render: (item: Blog) => <span>{item.title}</span>,
    },

    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div
      className={`min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900 ${className}`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenuCreator>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <HiOutlineNewspaper className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Mes Articles</h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary href="/dashboard/add-article" className="btn-primary-gradient" sizeClass="px-4 py-2">
                Ajouter un article
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par titre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search articles"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={() => {
                    setModalState({
                      ...modalState,
                      isDeleteModalOpen: false,
                      selectedItem: null,
                    });
                  }}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.title || "",
                  }}
                />
              )}

              <DynamicTable
                columns={columns}
                data={blogsList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "edit", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenuCreator>
    </div>
  );
};

export default ListArticles;
